import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "common/locales";
import { FieldArray, Formik, FormikErrors } from "formik";
import {
  AnimalGroup,
  FeedStocks,
  GENERAL_FEED_TYPE,
  GENERAL_FEED_UNITS,
  GeneralFeed,
  GeneralFeedEvent,
  useCurrentFarmAnimalGroups,
  useFeedStock,
} from "model";
import { MutableRefObject, ReactNode, useState } from "react";
import { useAppStore } from "stores";

import EnterDate from "features/general/EnterDate";
import { cloneDeep } from "lodash";
import * as Yup from "yup";
import EnterFeedEventFeedDetails from "./EnterFeedEventFeedDetails";
import { sumFeedForType } from "./feedCalculations";
import NotEnoughFeedDialog from "./NotEnoughFeedDialog";

const cardSx = {
  backgroundColor: "white",
  borderRadius: "8px",
  border: "1px solid rgba(0, 0, 0, 0.12)",
  p: 2.5,
};

interface Props {
  feedEvent: GeneralFeedEvent | undefined;
  handleSave: (generalFeedEvent: GeneralFeedEvent) => void;
  dirtyRef: MutableRefObject<boolean>;
  children: (args: {
    steps: { content: ReactNode }[];
    submitForm: () => Promise<void>;
  }) => ReactNode;
}

export default function EnterFeedEvent({
  feedEvent,
  handleSave,
  dirtyRef,
  children,
}: Props) {
  const { t } = useTranslation();
  const [notEnoughFeedDialogOpen, setNotEnoughFeedDialogOpen] = useState(false);

  const { data: allAnimalGroups, isLoading: animalGroupsLoading } =
    useCurrentFarmAnimalGroups();
  const currentFarmId = useAppStore((state) => state.currentFarmId);
  const { data: feedStocks, isLoading: feedStockLoading } = useFeedStock(
    currentFarmId ?? undefined
  );
  const loading = animalGroupsLoading || feedStockLoading;

  const checkEnoughFeedAvailable = (
    feeds: GeneralFeed[] | undefined,
    feedStocks: FeedStocks | undefined,
    oldFeeds: GeneralFeed[] | undefined
  ) => {
    for (const feedType of Object.values(GENERAL_FEED_TYPE)) {
      const feedTypeEnum = GENERAL_FEED_TYPE[feedType];
      const sumAmountNew = sumFeedForType(feeds, feedTypeEnum);
      const sumAmountOld = sumFeedForType(oldFeeds, feedTypeEnum);
      const availableStock = feedStocks?.feedStock.filter(
        (feedStock) => feedStock.type === feedTypeEnum
      );

      const availableStockAmount = availableStock
        ? availableStock?.length === 0
          ? 0
          : availableStock[0].amount
        : 0;
      if (sumAmountNew - sumAmountOld > availableStockAmount) {
        setNotEnoughFeedDialogOpen(true);
        return false;
      }
    }
    return true;
  };

  const validNumber = (min = 0) =>
    Yup.number()
      .typeError(t("edit.minNumber.error", { min }))
      .moreThan(min, t("edit.minNumber.error", { min }))
      .required(t("edit.required.error"));

  const FeedEventSchema = Yup.object().shape({
    note: Yup.string().optional(),
    animalGroupGuids: Yup.array().of(Yup.string()).required().min(1),
    feedEventDate: Yup.date(),
    feed: Yup.array()
      .of(
        Yup.object().shape({
          amount: validNumber(0).required(),
          feedType: Yup.mixed<GENERAL_FEED_TYPE>()
            .oneOf(Object.values(GENERAL_FEED_TYPE))
            .required(),
          unitType: Yup.mixed<GENERAL_FEED_UNITS>()
            .oneOf(Object.values(GENERAL_FEED_UNITS))
            .required(),
        })
      )
      .test(
        "sum-lte-stock",
        "The feed must be less that or equal available stock",
        (feeds: GeneralFeed[] | undefined) => {
          return checkEnoughFeedAvailable(feeds, feedStocks, feedEvent?.feed);
        }
      )
      .required()
      .min(1),
  });

  const newFeed = {
    amount: "",
  };

  if (loading) {
    return null;
  }

  const initialFeedEvent: Omit<GeneralFeedEvent, "feedEventDate"> & {
    feedEventDate: Date;
  } = {
    guid: "new",
    note: "",
    animalGroupGuids: [],
    feedEventDate: new Date(),
    feed: [
      {
        amount: "" as unknown as number,
        unitType: "" as GENERAL_FEED_UNITS,
        feedType: "" as GENERAL_FEED_TYPE,
      },
    ],
  };

  return (
    <Formik
      enableReinitialize={false}
      initialValues={{
        ...(feedEvent ?? initialFeedEvent),
        feedEventDate: feedEvent
          ? new Date(feedEvent.feedEventDate)
          : initialFeedEvent.feedEventDate,
      }}
      validationSchema={FeedEventSchema}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={(values) => {
        const enoughFeedAvailable = checkEnoughFeedAvailable(
          values.feed,
          feedStocks,
          feedEvent?.feed
        );
        if (!enoughFeedAvailable) {
          return;
        } else {
          void handleSave({
            ...values,
            feedEventDate: new Date(values.feedEventDate).toISOString(),
          } as GeneralFeedEvent);
        }
      }}
    >
      {({
        values,
        handleChange,
        errors,
        validateField,
        setFieldValue,
        dirty,
        submitForm,
      }) => {
        dirtyRef.current = dirty;
        const handleInputChange = async (
          e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
          fieldName: string
        ) => {
          await (handleChange(e) as unknown as Promise<void>);
          await (validateField(fieldName) as unknown as Promise<void>);
        };

        const handleAnimalGroupToggle = async (guid: string) => {
          const isSelected = (values.animalGroupGuids ?? []).includes(guid);
          const updated = isSelected
            ? values.animalGroupGuids.filter((g: string) => g !== guid)
            : [...values.animalGroupGuids, guid];
          await setFieldValue("animalGroupGuids", updated);
        };

        const steps = [
          {
            content: (
              <Container style={{ textAlign: "left" }}>
                <Grid container spacing={2.5}>
                  {/* Date */}
                  <Grid size={12}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      {t("feed.date.heading")}
                    </Typography>
                    <Box sx={cardSx}>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <EnterDate
                            disabled={false}
                            date={values.feedEventDate}
                            setDate={async (date: Date | null) => {
                              await setFieldValue("feedEventDate", date, false);
                            }}
                            dateError={!!errors.feedEventDate}
                            name={"feedEventDate"}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>

                  {/* Animal group checkbox list */}
                  <Grid size={12}>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 1,
                        color: errors.animalGroupGuids
                          ? "error.main"
                          : undefined,
                        fontWeight: 500,
                      }}
                    >
                      {t("feedEventInput.AnimalGroups.text")}
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "1fr 1fr",
                        },
                        borderRadius: "8px",
                        border: "1px solid",
                        borderColor: errors.animalGroupGuids
                          ? "error.main"
                          : "rgba(0, 0, 0, 0.12)",
                        backgroundColor: "white",
                        maxHeight: 220,
                        overflowY: "auto",
                        py: 0.5,
                        px: 1,
                      }}
                    >
                      {[...allAnimalGroups]
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((group: AnimalGroup) => {
                          const isSelected = (
                            values.animalGroupGuids ?? []
                          ).includes(group.guid);
                          return (
                            <FormControlLabel
                              key={group.guid}
                              control={
                                <Checkbox
                                  checked={isSelected}
                                  onChange={() =>
                                    handleAnimalGroupToggle(group.guid)
                                  }
                                  size="small"
                                  sx={{
                                    color: "rgba(0,0,0,0.3)",
                                    "&.Mui-checked": { color: "#7EC254" },
                                  }}
                                />
                              }
                              label={group.name}
                              sx={{
                                mx: 0,
                                "& .MuiFormControlLabel-label": {
                                  fontSize: "0.875rem",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                },
                              }}
                            />
                          );
                        })}
                    </Box>
                  </Grid>

                  {/* Feed details */}
                  <Grid size={12}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      {t("feed.details.heading")}
                    </Typography>
                    <Box sx={cardSx}>
                      <FieldArray name="feed">
                        {({ remove, push }) => (
                          <>
                            {values.feed.length > 0 &&
                              values.feed.map((food, index) => (
                                <EnterFeedEventFeedDetails
                                  key={index}
                                  handleInputChange={handleInputChange}
                                  index={index}
                                  errors={
                                    errors?.feed?.[index] as
                                      | FormikErrors<GeneralFeed>
                                      | undefined
                                  }
                                  singleFeed={food}
                                  remove={() => remove(index)}
                                />
                              ))}
                            <Button
                              onClick={() => push(cloneDeep(newFeed))}
                              sx={{
                                color: "#5E5E5E",
                                textTransform: "none",
                                mt: 1,
                              }}
                            >
                              + {t("feedEventInput.addFeed")}
                            </Button>
                          </>
                        )}
                      </FieldArray>
                    </Box>
                  </Grid>

                  {/* Additional details (optional) */}
                  <Grid size={12}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      {t("common.additionalDetails.heading")}
                    </Typography>
                    <Box sx={cardSx}>
                      <TextField
                        id="feed-event-input-note"
                        label={t("reseedEvent.notes.label")}
                        value={values.note}
                        helperText={errors.note}
                        error={!!errors.note}
                        fullWidth
                        onChange={(e) => handleInputChange(e, "note")}
                        name={"note"}
                        multiline
                        maxRows={12}
                      />
                    </Box>
                  </Grid>
                </Grid>

                <NotEnoughFeedDialog
                  feeds={values.feed}
                  oldFeeds={feedEvent?.feed}
                  feedStocks={feedStocks}
                  open={notEnoughFeedDialogOpen}
                  setClose={() => setNotEnoughFeedDialogOpen(false)}
                />
              </Container>
            ),
          },
        ];

        return <>{children({ steps, submitForm })}</>;
      }}
    </Formik>
  );
}
